<script setup lang="ts">
import { computed, ref, type PropType } from 'vue';
import GaiaButton from '../../../base/GaiaButton.vue';
import GaiaFormField from '../../../base/GaiaFormField.vue';
import GaiaInput from '../../../base/inputs/GaiaInput.vue';
import GaiaTextarea from '../../../base/inputs/GaiaTextarea.vue';
import GaiaRemovableList, {
  type GaiaRemovableItem,
} from '../../../composite/GaiaRemovableList.vue';
import { gaiaTextButtonOverrides } from '../../../themes/tokens';
import { TERMINATION_DATES_PROMPT, type CalendarWizardForm } from '@/composables/useCalendarWizard';

const props = defineProps({
  form: {
    type: Object as PropType<CalendarWizardForm>,
    required: true,
  },
  touched: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits<{ 'update:form': [form: CalendarWizardForm] }>();

function patch(changes: Partial<CalendarWizardForm>): void {
  emit('update:form', { ...props.form, ...changes });
}

const draft = ref<string>('');

const promptError = computed(() =>
  props.touched && props.form.prompt.trim().length === 0
    ? 'Describe the dates to extract before continuing'
    : undefined
);

// The index is the key: two examples may legitimately read the same, and a
// label-keyed list would then remove both.
const exampleItems = computed<GaiaRemovableItem[]>(() =>
  props.form.exampleData.map((entry, index) => ({ key: String(index), label: entry }))
);

function addExample(): void {
  const value = draft.value.trim();
  if (!value) return;
  patch({ exampleData: [...props.form.exampleData, value] });
  draft.value = '';
}

function removeExample(key: string): void {
  const index = Number(key);
  patch({ exampleData: props.form.exampleData.filter((_, position) => position !== index) });
}
</script>

<template>
  <n-flex vertical :size="18" :wrap="false">
    <gaia-form-field
      label="Prompt"
      required
      :error="promptError"
      hint="Ask for dates only. Insights returns nothing when the contract text doesn’t support a date."
    >
      <gaia-textarea
        :value="props.form.prompt"
        :autosize="{ minRows: 8, maxRows: 20 }"
        placeholder="Describe what to extract from the contracts"
        :status="promptError ? 'error' : undefined"
        style="width: 100%"
        @update:value="(value: string) => patch({ prompt: value })"
      />
      <template #action>
        <gaia-button
          text
          size="tiny"
          :theme-overrides="gaiaTextButtonOverrides.ink"
          @click="patch({ prompt: TERMINATION_DATES_PROMPT })"
        >
          Start from the termination-dates prompt
        </gaia-button>
      </template>
    </gaia-form-field>

    <gaia-form-field
      label="Example data"
      hint="Optional — sample answers that steer the extraction"
    >
      <n-flex vertical :size="10" :wrap="false" :style="{ width: '100%' }">
        <gaia-removable-list
          :items="exampleItems"
          empty-text="No examples yet."
          @remove="removeExample"
        />
        <n-flex align="center" :size="8" :wrap="false">
          <gaia-input
            v-model:value="draft"
            size="medium"
            placeholder="e.g. Termination date — 31 March 2027"
            :style="{ flex: '1 1 auto', minWidth: 0 }"
            @keydown.enter.prevent="addExample"
          />
          <gaia-button size="medium" :disabled="draft.trim().length === 0" @click="addExample">
            Add
          </gaia-button>
        </n-flex>
      </n-flex>
    </gaia-form-field>
  </n-flex>
</template>
